import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { PortalMeResult, PortalEnrollee, PortalNextMilestone, PortalCohortHub, PortalCohortSession, PortalOrientation } from '../api'
import { attestMilestone, dismissMilestone, confirmMilestone } from '../api'

// ── Helpers ───────────────────────────────────────────────────────────────────

function greeting(): string {
  const h = new Date().getHours()
  if (h < 12) return 'Good morning'
  if (h < 17) return 'Good afternoon'
  return 'Good evening'
}

function progressPct(e: PortalEnrollee): number {
  if (!e.progress.total) return 0
  return Math.round((e.progress.complete / e.progress.total) * 100)
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ── Orientation mode detection ─────────────────────────────────────────────────
// Fires once: day_of_arc === 0 and no milestones yet complete across all enrollees.

function isOrientationMode(me: PortalMeResult): boolean {
  if (me.enrollees.length === 0) return false
  const totalComplete = me.enrollees.reduce((s, e) => s + e.progress.complete, 0)
  return me.enrollees[0].day_of_arc === 0 && totalComplete === 0
}

// ── Up-next card (daily mode) ──────────────────────────────────────────────────

type CardActionState = 'idle' | 'busy' | 'done' | 'dismissed'

function UpNextCard({
  m,
  onAction,
}: {
  m: PortalNextMilestone
  onAction: (id: string, action: 'attest' | 'dismiss' | 'confirm') => Promise<void>
}) {
  const [state, setState] = useState<CardActionState>('idle')
  const navigate = useNavigate()

  const es = m.effective_state

  async function act(action: 'attest' | 'dismiss' | 'confirm') {
    setState('busy')
    try {
      await onAction(m.id, action)
      setState(action === 'dismiss' ? 'dismissed' : 'done')
    } catch {
      setState('idle')
    }
  }

  if (state === 'dismissed') {
    return (
      <div className="p-up-next-card p-up-next-dismissed">
        <div className="p-un-info">
          <div className="p-un-name p-muted p-strikethrough">{m.name}</div>
          <div className="p-un-meta">Dismissed</div>
        </div>
      </div>
    )
  }

  if (state === 'done') {
    return (
      <div className="p-up-next-card p-up-next-completed">
        <div className="p-un-dot p-un-dot-done">✓</div>
        <div className="p-un-info">
          <div className="p-un-program">{m.program_name}</div>
          <div className="p-un-name p-muted p-strikethrough">{m.name}</div>
          <div className="p-un-meta" style={{ color: 'var(--green)' }}>Done</div>
        </div>
      </div>
    )
  }

  const dotClass =
    es === 'overdue' ? 'p-un-dot p-un-dot-overdue' :
    es === 'proposed' ? 'p-un-dot p-un-dot-proposed' :
    'p-un-dot p-un-dot-active'

  const dotLabel = es === 'overdue' ? '!' : es === 'proposed' ? '?' : '→'

  const metaText =
    es === 'overdue' && m.target_at ? `Overdue — was due ${formatDate(m.target_at)}` :
    es === 'proposed' ? (m.proposed_at ? `Suggested ${formatDate(m.proposed_at)}` : 'Suggested by your coach') :
    m.target_at ? `Due ${formatDate(m.target_at)}` : ''

  return (
    <div
      className={`p-up-next-card${es === 'overdue' ? ' p-up-next-overdue' : ''}`}
      onClick={() => navigate('/roadmap/' + m.enrollee_id)}
      role="button"
      tabIndex={0}
      onKeyDown={ev => ev.key === 'Enter' && navigate('/roadmap/' + m.enrollee_id)}
    >
      <div className={dotClass}>{dotLabel}</div>
      <div className="p-un-info">
        <div className="p-un-program">{m.program_name}</div>
        <div className="p-un-name">{m.name}</div>
        {metaText && (
          <div className={`p-un-meta${es === 'overdue' ? ' p-un-meta-overdue' : ''}`}>{metaText}</div>
        )}
      </div>
      <div className="p-un-actions" onClick={ev => ev.stopPropagation()}>
        {(es === 'not_started' || es === 'overdue') && m.can_mark_done === false && m.action_url && (
          <a className="p-btn p-btn-primary p-btn-sm" href={m.action_url} target="_blank" rel="noopener noreferrer">
            {m.action_label ?? 'Open'}
          </a>
        )}
        {(es === 'not_started' || es === 'overdue') && m.can_mark_done !== false && (
          <>
            <button
              className="p-btn p-btn-primary p-btn-sm"
              disabled={state === 'busy'}
              onClick={() => act('attest')}
            >
              {state === 'busy' ? '…' : 'Mark complete'}
            </button>
            <button
              className="p-btn p-btn-ghost p-btn-sm"
              disabled={state === 'busy'}
              onClick={() => act('dismiss')}
            >
              Dismiss
            </button>
          </>
        )}
        {es === 'proposed' && (
          <>
            <button
              className="p-btn p-btn-primary p-btn-sm"
              disabled={state === 'busy'}
              onClick={() => act('confirm')}
            >
              {state === 'busy' ? '…' : 'Accept'}
            </button>
            <button
              className="p-btn p-btn-ghost p-btn-sm"
              disabled={state === 'busy'}
              onClick={() => act('dismiss')}
            >
              Not now
            </button>
          </>
        )}
      </div>
    </div>
  )
}

// ── Orientation view ──────────────────────────────────────────────────────────

function OrientationView({ me }: { me: PortalMeResult }) {
  const navigate = useNavigate()
  const firstName = me.person.first_name ?? me.person.display_name ?? me.person.primary_email.split('@')[0]
  const enrollee = me.enrollees[0]
  const programName = enrollee.program_label_override ?? enrollee.program_name

  return (
    <div className="p-page">
      {/* Hero */}
      <div className="p-orient-hero">
        <div className="p-orient-icon">🎉</div>
        <h1 className="p-orient-heading">You're all set, {firstName}.</h1>
        <p className="p-orient-sub">
          You're enrolled in <strong>{programName}</strong>. Here's a look at what's ahead so you can hit the ground running.
        </p>
      </div>

      {/* First steps preview */}
      {me.next_milestones.length > 0 && (
        <div className="p-orient-section">
          <div className="p-section-label">Your first steps</div>
          <div className="p-first-steps">
            {me.next_milestones.slice(0, 4).map((m, i) => (
              <div key={m.id} className="p-first-step">
                <div className="p-first-step-num">{i + 1}</div>
                <div className="p-first-step-info">
                  <div className="p-first-step-name">{m.name}</div>
                  {m.user_facing_description && (
                    <div className="p-first-step-desc">{m.user_facing_description}</div>
                  )}
                  {m.target_at && (
                    <div className="p-first-step-meta">Due {formatDate(m.target_at)}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="p-orient-cta">
        <button
          className="p-btn p-btn-cta"
          onClick={() => navigate('/roadmap/' + enrollee.id)}
        >
          View your full roadmap →
        </button>
      </div>
    </div>
  )
}

// ── First-week view (orientation programs, build B) ───────────────────────────
// A member's or BKOS buyer's first week: every step of every orientation
// program they hold, defined in Command Centre. Only a self-attest step gets
// "Mark done"; the rest tick themselves off from their own signal (the team
// building the workspace, the first live call).

function FirstWeekStep({ step, n }: { step: PortalOrientation['steps'][number]; n: number }) {
  const [state, setState] = useState(step.state)
  const [busy, setBusy] = useState(false)
  const done = state === 'complete'

  async function markDone() {
    setBusy(true)
    try {
      await attestMilestone(step.id)
      setState('complete')
    } catch {
      /* leave it open; the member can try again */
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`p-first-step${done ? ' p-first-step-done' : ''}`}>
      <div className="p-first-step-num">{done ? '✓' : n}</div>
      <div className="p-first-step-info">
        <div className={`p-first-step-name${done ? ' p-strikethrough p-muted' : ''}`}>{step.name}</div>
        {step.description && <div className="p-first-step-desc">{step.description}</div>}
        {done
          ? <div className="p-first-step-meta" style={{ color: 'var(--green)' }}>Done</div>
          : !step.can_mark_done && (
            <div className="p-first-step-meta">
              {step.action_url ? 'Ticks itself off once you have done it' : 'Ticks itself off when it happens'}
            </div>
          )}
      </div>
      {!done && !step.can_mark_done && step.action_url && (
        <a className="p-btn p-btn-primary p-btn-sm" href={step.action_url} target="_blank" rel="noopener noreferrer">
          {step.action_label ?? 'Open'}
        </a>
      )}
      {!done && step.can_mark_done && (
        <button className="p-btn p-btn-primary p-btn-sm" disabled={busy} onClick={() => void markDone()}>
          {busy ? '…' : 'Mark done'}
        </button>
      )}
    </div>
  )
}

function FirstWeekView({ me, programs }: { me: PortalMeResult; programs: PortalOrientation[] }) {
  const firstName = me.person.first_name ?? me.person.display_name ?? me.person.primary_email.split('@')[0]
  const total = programs.reduce((s, p) => s + p.steps.length, 0)
  const complete = programs.reduce((s, p) => s + p.steps.filter(st => st.state === 'complete').length, 0)
  const allDone = total > 0 && complete === total

  return (
    <div className="p-page">
      <div className="p-orient-hero">
        <div className="p-orient-icon">🎉</div>
        <h1 className="p-orient-heading">Welcome, {firstName}.</h1>
        <p className="p-orient-sub">
          {allDone
            ? "You've done everything on your first-week list. Nicely done."
            : `Here's your first week. ${complete} of ${total} done so far.`}
        </p>
      </div>

      {programs.map(p => (
        <div key={p.enrollee_id} className="p-orient-section">
          <div className="p-section-label">{p.program_name}</div>
          <div className="p-first-steps">
            {p.steps.map((st, i) => <FirstWeekStep key={st.id} step={st} n={i + 1} />)}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Daily view ────────────────────────────────────────────────────────────────

function DailyView({ me }: { me: PortalMeResult }) {
  const navigate = useNavigate()
  const firstName = me.person.first_name ?? me.person.display_name ?? me.person.primary_email.split('@')[0]

  const totalOverdue = me.enrollees.reduce((s, e) => s + e.progress.overdue, 0)
  const pendingUpNext = me.next_milestones

  async function handleAction(id: string, action: 'attest' | 'dismiss' | 'confirm') {
    if (action === 'attest') await attestMilestone(id)
    else if (action === 'dismiss') await dismissMilestone(id)
    else await confirmMilestone(id)
  }

  return (
    <div className="p-page">
      {/* Header */}
      <div className="p-dash-header">
        <div className="p-dash-eyebrow">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</div>
        <h1 className="p-dash-heading">{greeting()}, {firstName}.</h1>
        {pendingUpNext.length > 0
          ? <p className="p-dash-sub">You have {pendingUpNext.length} thing{pendingUpNext.length !== 1 ? 's' : ''} waiting for you.</p>
          : <p className="p-dash-sub">You're all caught up.</p>
        }
      </div>

      {/* Overdue attention banner */}
      {totalOverdue > 0 && (
        <div className="p-attention-banner">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M8 5v4M8 11v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>
            <strong>{totalOverdue} milestone{totalOverdue !== 1 ? 's' : ''} overdue</strong>
            {totalOverdue === 1 ? ' — needs your attention' : ' — need your attention'}
          </span>
        </div>
      )}

      {/* Up next */}
      {pendingUpNext.length > 0 && (
        <>
          <div className="p-section-label">Up next</div>
          <div className="p-up-next-cards">
            {pendingUpNext.map(m => (
              <UpNextCard key={m.id} m={m} onAction={handleAction} />
            ))}
          </div>
        </>
      )}

      {/* Program cards (compact) */}
      {me.enrollees.length > 0 && (
        <>
          <div className="p-section-label">Your programs</div>
          <div className="p-program-cards-compact">
            {me.enrollees.map(e => {
              const pct = progressPct(e)
              const name = e.program_label_override ?? e.program_name
              return (
                <div
                  key={e.id}
                  className="p-program-card-compact"
                  onClick={() => navigate('/roadmap/' + e.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={ev => ev.key === 'Enter' && navigate('/roadmap/' + e.id)}
                >
                  <div className="p-program-compact-body">
                    <div className="p-program-compact-name">{name}</div>
                    <div className="p-program-compact-progress">
                      <div className="p-progress-bar-wrap">
                        <div className="p-progress-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="p-progress-label">{e.progress.complete} / {e.progress.total}</div>
                    </div>
                    {e.progress.overdue > 0 && (
                      <div className="p-overdue-note">
                        {e.progress.overdue} milestone{e.progress.overdue > 1 ? 's' : ''} overdue
                      </div>
                    )}
                  </div>
                  <div className="p-program-compact-arrow">›</div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {me.enrollees.length === 0 && (
        <div className="p-empty">No active programs yet. Check back once you're enrolled.</div>
      )}
    </div>
  )
}

// ── Cohort Hub ────────────────────────────────────────────────────────────────

const TAKER_BASE = 'https://discover.thesuccessfulbookkeeper.com'

type SessionRowKind = 'done' | 'replay' | 'next' | 'scheduled' | 'skip'

interface ScheduleRow {
  kind: SessionRowKind
  session?: PortalCohortSession
  skipDate?: string  // formatted label for vacation rows
}

function buildScheduleRows(sessions: PortalCohortSession[]): ScheduleRow[] {
  const now = Date.now()
  const rows: ScheduleRow[] = []
  let foundNext = false

  for (let i = 0; i < sessions.length; i++) {
    const s = sessions[i]
    const prev = sessions[i - 1]

    // Insert a vacation row when there's a 10+ day gap between consecutive sessions
    if (prev?.scheduled_at && s.scheduled_at) {
      const gapDays = (new Date(s.scheduled_at).getTime() - new Date(prev.scheduled_at).getTime()) / 86400000
      if (gapDays >= 10) {
        // Compute the skipped Tuesday (7 days after prev)
        const skippedMs = new Date(prev.scheduled_at).getTime() + 7 * 86400000
        const skipped = new Date(skippedMs)
        const skipLabel = skipped.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
        rows.push({ kind: 'skip', skipDate: skipLabel })
      }
    }

    const sessionMs = s.scheduled_at ? new Date(s.scheduled_at).getTime() : null
    const isPast = sessionMs !== null && sessionMs < now

    let kind: SessionRowKind
    if (isPast && s.replay_state === 'live') {
      kind = 'replay'
    } else if (isPast) {
      kind = 'done'
    } else if (!foundNext) {
      kind = 'next'
      foundNext = true
    } else {
      kind = 'scheduled'
    }
    rows.push({ kind, session: s })
  }
  return rows
}

function sessionLabel(s: PortalCohortSession): string {
  // Strip "Week N: " prefix — the week number dot already shows it
  return s.title.replace(/^Week\s+\d+:\s*/i, '')
}

function fmtScheduledAt(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function fmtTime(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })
}

function replayCount(sessions: PortalCohortSession[]): number {
  return sessions.filter(s => s.replay_state === 'live').length
}

function nextSession(rows: ScheduleRow[]): PortalCohortSession | undefined {
  return rows.find(r => r.kind === 'next')?.session
}

function CohortHubView({ me, enrollee, hub }: { me: PortalMeResult; enrollee: PortalEnrollee; hub: PortalCohortHub }) {
  const firstName = me.person.first_name ?? me.person.display_name ?? me.person.primary_email.split('@')[0]
  const programName = enrollee.program_label_override ?? enrollee.program_name
  const bannerKey = `hub-banner-dismissed-${hub.cohort_id}`
  const [bannerVisible, setBannerVisible] = useState(() => {
    return hub.announcement ? !localStorage.getItem(bannerKey) : false
  })

  function dismissBanner() {
    localStorage.setItem(bannerKey, '1')
    setBannerVisible(false)
  }

  const rows = buildScheduleRows(hub.sessions)
  const next = nextSession(rows)
  const replays = replayCount(hub.sessions)
  const totalSessions = hub.sessions.length
  const completedSessions = hub.sessions.filter(s => {
    const ms = s.scheduled_at ? new Date(s.scheduled_at).getTime() : null
    return ms !== null && ms < Date.now()
  }).length

  // "Open the course" link: derive community base from first session with a recording_url
  const communityUrl = hub.community_url ?? 'https://community.thesuccessfulbookkeeper.com'
  const firstRecording = hub.sessions.find(s => s.recording_url)?.recording_url ?? null
  const courseUrl = firstRecording
    ? firstRecording.replace(/\/p1-module-.*$/, '')
    : `${communityUrl}/learning`

  // Progress: weeks complete out of total (excluding vacation rows)
  const weekNum = completedSessions + 1 > totalSessions ? totalSessions : completedSessions + 1
  const pct = Math.round((completedSessions / totalSessions) * 100)

  return (
    <div className="p-hub-wrap">
      {bannerVisible && hub.announcement && (
        <div className="p-hub-banner">
          <span className="p-hub-banner-pin">📌</span>
          <span className="p-hub-banner-text">{hub.announcement}</span>
          <button className="p-hub-banner-x" onClick={dismissBanner} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="p-hub-shell">
        <div className="p-hub-greet-label">Welcome back</div>
        <div className="p-hub-greet-name">Hi {firstName}</div>

        {/* Program header */}
        <div className="p-hub-prog-card">
          <img
            className="p-hub-prog-photo"
            src={`https://pub-21e4931ef4774e378bca73afe99a4ba4.r2.dev/people/lisa-campbell.png`}
            alt="Lisa Campbell"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
          <div className="p-hub-prog-info">
            <div className="p-hub-prog-title">{programName}</div>
            <div className="p-hub-prog-sub">{hub.cohort_name} · Tuesdays at 3:00 PM ET</div>
          </div>
          <div className="p-hub-prog-progress">
            <div className="p-hub-pp-week">Week {weekNum} of {totalSessions}</div>
            <div className="p-hub-bar"><span style={{ width: `${pct}%` }} /></div>
            <div className="p-hub-pp-pct">{completedSessions} session{completedSessions !== 1 ? 's' : ''} complete</div>
          </div>
        </div>

        {/* Quick actions */}
        <div className="p-hub-qa">
          {next ? (
            <a
              className="p-hub-qa-card"
              href={next.zoom_join_url ?? '#'}
              target={next.zoom_join_url ? '_blank' : undefined}
              rel="noreferrer"
            >
              <div className="p-hub-qa-ic p-hub-qa-ic-cta">▶</div>
              <div className="p-hub-qa-label">Join the next live call</div>
              <div className="p-hub-qa-meta">{fmtScheduledAt(next.scheduled_at)} · {fmtTime(next.scheduled_at)}</div>
            </a>
          ) : (
            <div className="p-hub-qa-card p-hub-qa-card-muted">
              <div className="p-hub-qa-ic p-hub-qa-ic-blue">✓</div>
              <div className="p-hub-qa-label">All sessions complete</div>
              <div className="p-hub-qa-meta">Program wrap complete</div>
            </div>
          )}
          <a className="p-hub-qa-card" href={courseUrl} target="_blank" rel="noreferrer">
            <div className="p-hub-qa-ic p-hub-qa-ic-blue">📚</div>
            <div className="p-hub-qa-label">Open the course</div>
            <div className="p-hub-qa-meta">Lessons &amp; workbooks in the community</div>
          </a>
          <a className="p-hub-qa-card" href={`${TAKER_BASE}/replays`} target="_blank" rel="noreferrer">
            <div className="p-hub-qa-ic p-hub-qa-ic-green">🎬</div>
            <div className="p-hub-qa-label">Watch replays</div>
            <div className="p-hub-qa-meta">{replays} session{replays !== 1 ? 's' : ''} available</div>
          </a>
        </div>

        <div className="p-hub-cols">
          {/* Schedule */}
          <div>
            <div className="p-hub-sec-h">Your schedule <span className="p-hub-count">{totalSessions} weeks</span></div>
            <div className="p-hub-sched">
              {rows.map((row, i) => {
                if (row.kind === 'skip') {
                  return (
                    <div key={`skip-${i}`} className="p-hub-row p-hub-row-skip">
                      <div className="p-hub-dot p-hub-dot-skip">✈</div>
                      <div className="p-hub-row-main">
                        <div className="p-hub-row-title">No call — Lisa away</div>
                        <div className="p-hub-row-date">{row.skipDate}</div>
                      </div>
                      <span className="p-hub-chip p-hub-chip-skip">No session</span>
                    </div>
                  )
                }
                const s = row.session!
                const isReplay = row.kind === 'replay'
                const isDone = row.kind === 'done'
                const isNext = row.kind === 'next'

                let replayHref: string | undefined
                if (s.replay_smart_link_slug) {
                  replayHref = `${TAKER_BASE}/replays/${s.replay_smart_link_slug}`
                } else if (s.recording_url) {
                  replayHref = s.recording_url
                }

                return (
                  <div key={s.id} className={`p-hub-row${isNext ? ' p-hub-row-next' : ''}`}>
                    {(isReplay || isDone) ? (
                      <div className="p-hub-dot p-hub-dot-done">✓</div>
                    ) : isNext ? (
                      <div className="p-hub-dot p-hub-dot-next">{s.sort_order}</div>
                    ) : (
                      <div className="p-hub-dot p-hub-dot-sched">{s.sort_order}</div>
                    )}
                    <div className="p-hub-row-main">
                      <div className="p-hub-row-title">{sessionLabel(s)}</div>
                      <div className="p-hub-row-date">
                        {fmtScheduledAt(s.scheduled_at)}
                        {isNext && s.scheduled_at ? ` · ${fmtTime(s.scheduled_at)}` : ''}
                      </div>
                    </div>
                    {isReplay && (
                      <>
                        <span className="p-hub-chip p-hub-chip-done">Watched</span>
                        {replayHref && <a className="p-hub-row-act" href={replayHref} target="_blank" rel="noreferrer">Replay →</a>}
                      </>
                    )}
                    {isDone && (
                      <>
                        <span className="p-hub-chip p-hub-chip-sched">Replay ready</span>
                        {replayHref && <a className="p-hub-row-act" href={replayHref} target="_blank" rel="noreferrer">Watch →</a>}
                      </>
                    )}
                    {isNext && (
                      <>
                        <span className="p-hub-chip p-hub-chip-next">Up next</span>
                        {s.zoom_join_url && <a className="p-hub-row-act" href={s.zoom_join_url} target="_blank" rel="noreferrer">Join →</a>}
                      </>
                    )}
                    {row.kind === 'scheduled' && (
                      <span className="p-hub-chip p-hub-chip-sched">Scheduled</span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Sidebar */}
          <div className="p-hub-aside">
            <div className="p-hub-aside-card">
              <div className="p-hub-aside-h">Your progress</div>
              <div className="p-hub-stat"><span>Sessions complete</span><span className="p-hub-stat-v">{completedSessions} of {totalSessions}</span></div>
              <div className="p-hub-stat"><span>Replays available</span><span className="p-hub-stat-v">{replays}</span></div>
              <div className="p-hub-stat"><span>Program standing</span><span className="p-hub-stat-v p-hub-stat-good">On track</span></div>
            </div>

            <div className="p-hub-aside-card">
              <div className="p-hub-aside-h">Where to go</div>
              <div className="p-hub-links">
                <a href={courseUrl} target="_blank" rel="noreferrer">
                  <span className="p-hub-li">📚</span> The course in the community <span className="p-hub-arr">→</span>
                </a>
                <a href={`${TAKER_BASE}/replays`} target="_blank" rel="noreferrer">
                  <span className="p-hub-li">🎬</span> All replays <span className="p-hub-arr">→</span>
                </a>
                <a href={communityUrl} target="_blank" rel="noreferrer">
                  <span className="p-hub-li">💬</span> Community discussion <span className="p-hub-arr">→</span>
                </a>
              </div>
            </div>

            <div className="p-hub-aside-card p-hub-aside-support">
              <div className="p-hub-aside-h">Need a hand?</div>
              <p>Question about a session, your access, or the program? We're one message away.</p>
              <button
                className="p-btn p-btn-cta p-btn-full"
                onClick={() => {/* support modal — wired in next phase */}}
              >
                Contact support
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Dashboard (root) ──────────────────────────────────────────────────────────

export default function Dashboard({ me: meAll }: { me: PortalMeResult }) {
  // Orientation programs are a first week, not a program: someone who holds
  // only those sees the first-week view; anyone with a real program sees the
  // program views, which read only their real enrollees.
  const orientation = meAll.orientation ?? []
  const realEnrollees = meAll.enrollees.filter(e => e.program_kind !== 'orientation')
  if (realEnrollees.length === 0 && orientation.length > 0) {
    return <FirstWeekView me={meAll} programs={orientation} />
  }
  const me: PortalMeResult = { ...meAll, enrollees: realEnrollees }
  // If any enrollee has a cohort hub with sessions, show the hub for the first one
  const cohortEnrollee = me.enrollees.find(e => e.cohort_hub && e.cohort_hub.sessions.length > 0)
  if (cohortEnrollee?.cohort_hub) {
    return <CohortHubView me={me} enrollee={cohortEnrollee} hub={cohortEnrollee.cohort_hub} />
  }
  if (isOrientationMode(me)) return <OrientationView me={me} />
  return <DailyView me={me} />
}
